'use client';

import { Button } from '@onbeam/business-ui';
import useEmblaCarousel from 'embla-carousel-react';
import {
  useState,
  useCallback,
  useEffect,
  KeyboardEvent,
  PropsWithChildren,
} from 'react';
import { CarouselProps, CarouselApi } from './Carousel.types';
import { css, cx } from '@onbeam/business-styled-system/css';
import { CarouselContext, useCarousel } from './Carousel.utils';
import { flex } from '@onbeam/business-styled-system/patterns';
import { ArrowLeftIcon } from '@onbeam/icons/business';

export const Carousel = ({ className, children, ...props }: CarouselProps) => {
  const [carouselRef, api] = useEmblaCarousel({
    axis: 'x',
    skipSnaps: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((api: CarouselApi) => {
    if (!api) return;

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  useEffect(() => {
    if (!api) return;

    const middleIndex = Math.floor(api.slideNodes().length / 2);
    api.scrollTo(middleIndex);

    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <section
        onKeyDownCapture={handleKeyDown}
        className={cx(css({ position: 'relative', w: 'full' }), className)}
        aria-roledescription="carousel"
        {...props}
      >
        {children}
      </section>
    </CarouselContext.Provider>
  );
};

export const CarouselContent = ({
  className,
  ...props
}: PropsWithChildren<{ className?: string }>) => {
  const { carouselRef } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className={flex({ overflow: 'hidden', justify: 'center' })}
    >
      <div className={cx(flex(), className)} {...props} />
    </div>
  );
};

export const CarouselItem = ({
  className,
  ...props
}: PropsWithChildren<{ className?: string }>) => (
  <div
    role="group"
    aria-roledescription="slide"
    className={cx(
      css({
        minW: '[0]',
        flexShrink: '0',
        flexGrow: '0',
      }),
      className,
    )}
    {...props}
  />
);

export const CarouselPrevious = ({ className }: { className?: string }) => {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      variant="secondary"
      size="md"
      className={cx(
        css({
          rounded: 'full',
          p: '2.5',
        }),
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
    >
      <ArrowLeftIcon height={16} width={16} />
      <span className={css({ srOnly: true })}>Previous slide</span>
    </Button>
  );
};

export const CarouselNext = ({ className }: { className?: string }) => {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      variant="secondary"
      size="md"
      className={cx(
        css({
          rounded: 'full',
          p: '2.5',
        }),
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
    >
      <ArrowLeftIcon
        height={16}
        width={16}
        className={css({ transform: 'rotate(180deg)' })}
      />
      <span className={css({ srOnly: true })}>Next slide</span>
    </Button>
  );
};

export const CarouselControls = ({ className }: { className?: string }) => (
  <div className={cx(flex({ gap: '3', align: 'center' }), className)}>
    <CarouselPrevious />
    <CarouselNext />
  </div>
);
